import { Link } from 'react-router-dom';
import { MapPin, Heart, Award, Users, Calendar, Send, Phone } from 'lucide-react';
import { Card } from '../../../shared/components/ui/card';
import { Button } from '../../../shared/components/ui/button';
import { Badge } from '../../../shared/components/ui/badge';
import { useAuth } from '../../authentication/hooks/useAuth';
import { useWorkspace } from '@/features/workspace-sub-delegation';
import { useGuestSignupModal } from '../../pre-login/context/GuestSignupModalContext';
import { useUnauthRumAction } from '../../pre-login/hooks/useUnauthRumAction';
import { useAddToNetwork, useRemoveFromNetwork, useCheckConnection } from '../../network/hooks/useNetwork';
import { useAddFavoriteProfessional, useRemoveFavoriteProfessional, useProfessionalFavoriteStatus } from '../../favorites/hooks/useFavorites';
import { useSpMessageLaunch } from '@/features/messaging/hooks/useSpMessageLaunch';
import { useState } from 'react';
import { getProviderDisplayName, getProviderDisplayInitial } from '../utils/formatters';
import { ShareContactCardModal } from '@/features/contact-card-share/components/ShareContactCardModal';
import { useContactAction } from '../hooks/useContactAction';
import type { ProviderCardProps } from './ProviderCard.types';

const formatServiceRange = (range: string): string => {
  const rangeMap: Record<string, string> = {
    IN_HOME: 'In-Home',
    VIRTUAL: 'Virtual',
    IN_OFFICE: 'In-Office',
  };
  return rangeMap[range] || range;
};

export const ProviderCard = ({
  id: providerId,
  searchContextId,
  userId,
  providerType = 'SERVICE_PROVIDER',
  companyName,
  businessAddress,
  hideBusinessAddress = false,
  distance,
  specialties,
  serviceRanges,
  profilePhoto,
  verified = false,
  isInNetwork: isInNetworkProp,
  isFavorited: isFavoritedProp,
  partnerCompany,
  user: providerUser,
  onSharePromptOpen,
  hasClients = false,
  canSchedule = false,
  onSchedule,
  calendlyUrl,
  intakeFormUrl,
  advisorBiography,
}: ProviderCardProps) => {
  const { user, isAuthenticated } = useAuth();
  const { openModal: openGuestModal } = useGuestSignupModal();
  const trackAction = useUnauthRumAction();
  // Delegates act under the workspace owner's role + WORKSPACE_OPERATIONS grant.
  const { effectiveRole, hasWorkspacePermission } = useWorkspace();
  const role = effectiveRole ?? user?.role;
  const canOperate = hasWorkspacePermission('WORKSPACE_OPERATIONS');
  const isFinancialAdvisor = providerType === 'FINANCIAL_ADVISOR';
  const isGuest = !isAuthenticated;

  // Gate for "View Details" → opens the provider/advisor profile. SP profiles
  // are public (feature 076-03), so guests navigate straight to /providers/:id
  // — the RUM event still fires for the funnel. FA profiles stay login-only:
  // guests get the signup modal with a /advisors/:id post-signup deep link.
  const handleGuestProfileGate = (e: React.MouseEvent) => {
    if (isGuest) {
      trackAction('unauth.provider_profile_click', { providerId });
      if (isFinancialAdvisor) {
        e.preventDefault();
        openGuestModal({
          trigger: 'provider_profile_click',
          triggerEntityId: providerId,
          providerType: 'FINANCIAL_ADVISOR',
        });
      }
    }
  };

  // Gate for "Contact" → messaging action. Distinct from the profile gate so
  // the post-signup deep link doesn't drop the user on the profile page when
  // they intended to message (spec Item 2: action_button_click for Message /
  // Schedule / Favorite).
  const handleGuestContactGate = (e: React.MouseEvent) => {
    if (isGuest) {
      e.preventDefault();
      trackAction('unauth.action_button_click', {
        action: 'message',
        entityType: isFinancialAdvisor ? 'financial_advisor' : 'service_provider',
        entityId: providerId,
      });
      openGuestModal({ trigger: 'action_button_click', triggerEntityId: providerId });
    }
  };

  // FR-007e: Display name fallback chain - partnerCompany.name → companyName → (firstName + lastName)
  const baseDisplayName = getProviderDisplayName(partnerCompany, companyName, providerUser);
  const baseDisplayInitial = getProviderDisplayInitial(partnerCompany, companyName, providerUser);

  const fpPersonName = providerUser?.firstName
    ? `${providerUser.firstName}${providerUser.lastName ? ` ${providerUser.lastName}` : ''}`.trim()
    : '';
  const fpCompanyLabel = partnerCompany?.name || companyName || '';
  const displayName = isFinancialAdvisor && fpPersonName ? fpPersonName : baseDisplayName;
  const displayInitial =
    isFinancialAdvisor && fpPersonName
      ? (fpPersonName.charAt(0).toUpperCase() || '?')
      : baseDisplayInitial;
  const advisorCompanySecondary =
    isFinancialAdvisor && fpPersonName && fpCompanyLabel && fpCompanyLabel !== fpPersonName ? fpCompanyLabel : '';
  const canAddToNetwork = (role === 'SERVICE_PROVIDER' || role === 'FINANCIAL_ADVISOR') && canOperate;
  // Feature 071: both pro roles, and only for a genuine Service Provider — this
  // list also renders advisors, whose cards have no contact card to send.
  //
  // The SP gets the same button here rather than the mode toggle the pre-spec
  // put inside SPReferralModal: on this page that modal is handed a *User* id by
  // `onSharePromptOpen`, and the card needs the ServiceProvider profile id. The
  // card itself holds both, so the direct action is the one that can work.
  const canSendContactCard =
    (role === 'FINANCIAL_ADVISOR' || role === 'SERVICE_PROVIDER') &&
    canOperate &&
    providerType === 'SERVICE_PROVIDER';
  const shouldFetchNetwork = canAddToNetwork && isInNetworkProp === undefined;
  const { data: connectionData } = useCheckConnection(userId, shouldFetchNetwork);
  const addToNetworkMutation = useAddToNetwork();
  const removeFromNetworkMutation = useRemoveFromNetwork();
  const [contactCardOpen, setContactCardOpen] = useState(false);
  const [localNetworkState, setLocalNetworkState] = useState<boolean | null>(null);
  const currentNetworkState =
    localNetworkState !== null
      ? localNetworkState
      : isInNetworkProp !== undefined
        ? (isInNetworkProp || false)
        : (connectionData?.isConnected || false);

  const handleNetworkToggle = async () => {
    if (!canAddToNetwork) return;
    const wasInNetwork = currentNetworkState;
    setLocalNetworkState(!currentNetworkState);
    try {
      if (currentNetworkState) {
        await removeFromNetworkMutation.mutateAsync(userId);
      } else {
        await addToNetworkMutation.mutateAsync(userId);

        // Feature 047: Show prompt if just added and has clients
        if (!wasInNetwork && hasClients && onSharePromptOpen) {
          onSharePromptOpen({ id: userId, name: displayName });
        }
      }
    } catch (error) {
      setLocalNetworkState(null);
      console.error('Failed to update network:', error);
    }
  };

  const canFavorite = role === 'CARE_SEEKER' && canOperate;
  const shouldFetchFavorite = canFavorite && isFavoritedProp === undefined;
  const { data: favoriteStatusData } = useProfessionalFavoriteStatus(userId, shouldFetchFavorite);
  const addFavoriteMutation = useAddFavoriteProfessional();
  const removeFavoriteMutation = useRemoveFavoriteProfessional();
  const [localFavoriteState, setLocalFavoriteState] = useState<boolean | null>(null);
  const currentFavoriteState =
    localFavoriteState !== null
      ? localFavoriteState
      : isFavoritedProp !== undefined
        ? (isFavoritedProp || false)
        : (favoriteStatusData?.isFavorite || false);

  const handleFavoriteToggle = async () => {
    if (!canFavorite) return;
    const newState = !currentFavoriteState;
    setLocalFavoriteState(newState);
    try {
      if (currentFavoriteState) {
        await removeFavoriteMutation.mutateAsync(userId);
      } else {
        await addFavoriteMutation.mutateAsync({ professionalUserId: userId });
      }
    } catch (error) {
      setLocalFavoriteState(null);
      console.error('Failed to update favorite:', error);
    }
  };

  // Display specialties as comma-separated text below name
  const specialtiesText = specialties?.slice(0, 3)?.map(s => s.specialty.name)?.join(', ') || '';

  // Limit specialties to 2 for badge display
  const displayedSpecialties = specialties.slice(0, 2);

  // Format service ranges
  const formattedServiceRanges = serviceRanges
    .map(formatServiceRange)
    .join(', ');

  // Round distance to 1 decimal place
  const formattedDistance = distance
    ? distance.toFixed(1)
    : null;

  const { launch: launchMessage, modal: messageModal } = useSpMessageLaunch({
    serviceProviderId: providerId,
    providerName: displayName,
    sourceSurface: 'provider_card',
  });

  // Feature 103 (The Key pilot): "Contact Provider" and "Schedule" both route
  // to the embedded intake form for eligible care seekers when the provider
  // has one configured. FP/advisor cards are never eligible.
  const contactAction = useContactAction(
    { intakeFormUrl, calendlyUrl },
    canSchedule && !isFinancialAdvisor,
  );

  const contactHref = `/messages?recipient=${userId}`;
  const detailsHref = isFinancialAdvisor
    ? `/advisors/${providerId}`
    : `/providers/${providerId}${searchContextId
      ? `?searchContextId=${encodeURIComponent(searchContextId)}&discoverySource=DIRECTORY_SEARCH`
      : ''}`;
  const contactLabel = isFinancialAdvisor ? 'Contact Advisor' : 'Contact Provider';

  const handleContactClick = (e: React.MouseEvent) => {
    handleGuestContactGate(e);
    if (isGuest) return;
    if (contactAction.primary === 'intake-form' && onSchedule) {
      onSchedule({ id: providerId, name: displayName, calendlyUrl: calendlyUrl || null, intakeFormUrl: intakeFormUrl || null });
      return;
    }
    void launchMessage();
  };

  return (
    <Card
      className="overflow-hidden hover:shadow-lg transition-shadow"
      role="article"
    >
      <div className="grid md:grid-cols-4 gap-4">
        <div className="md:col-span-1">
          {profilePhoto ? (
            <img
              src={profilePhoto}
              alt={`${displayName} profile`}
              loading="lazy"
              className="w-full h-full object-contain min-h-48 bg-gray-50"
            />
          ) : (
            <div
              data-testid="logo-placeholder"
              className="w-full h-full min-h-48 bg-gradient-to-br from-[#026857] to-[#378F94] flex items-center justify-center"
            >
              <div className="text-white text-4xl font-bold">
                {displayInitial}
              </div>
            </div>
          )}
        </div>

        <div className="md:col-span-3 p-6">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xl text-gray-900">{displayName}</h3>
                {isFinancialAdvisor && (
                  <Badge variant="secondary">
                    Financial Advisor
                  </Badge>
                )}
                {verified && (
                  <Badge className="bg-[#026857] hover:bg-[#026857]">
                    <Award className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>

              {advisorCompanySecondary && (
                <p className="text-sm text-gray-600 mb-2">{advisorCompanySecondary}</p>
              )}

              {specialtiesText && (
                <p className="text-sm text-gray-600 mb-2">{specialtiesText}</p>
              )}

              <div className="flex items-center gap-4 text-sm text-gray-600">
                {!hideBusinessAddress && businessAddress && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {businessAddress}
                  </div>
                )}
                {formattedDistance && (
                  <div className="flex items-center gap-1">
                    {!hideBusinessAddress && businessAddress && '• '}
                    {formattedDistance} mi
                  </div>
                )}
              </div>

              {formattedServiceRanges && (
                <div className="text-sm text-gray-600 mt-1">
                  {formattedServiceRanges}
                </div>
              )}

              {isFinancialAdvisor && advisorBiography && (
                <p
                  className="text-sm text-gray-700 mt-2 line-clamp-2"
                  title={advisorBiography}
                  data-testid="advisor-biography-excerpt"
                >
                  {advisorBiography}
                </p>
              )}
            </div>

            {canAddToNetwork && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNetworkToggle}
                disabled={addToNetworkMutation.isPending || removeFromNetworkMutation.isPending}
                className={currentNetworkState ? 'text-[#026857]' : 'text-gray-400'}
                aria-label={currentNetworkState ? 'Remove from my network' : 'Add provider to my network'}
                title={currentNetworkState ? 'Remove from my network' : 'Add provider to my network'}
              >
                <Users
                  className={`w-5 h-5 ${currentNetworkState ? 'fill-[#026857]' : ''}`}
                />
              </Button>
            )}
            {canSendContactCard && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setContactCardOpen(true)}
                className="text-gray-400"
                aria-label="Send this provider's contact card to someone off-platform"
                title="Send contact card"
              >
                <Send className="w-5 h-5" aria-hidden="true" />
              </Button>
            )}
            {canFavorite && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleFavoriteToggle}
                disabled={addFavoriteMutation.isPending || removeFavoriteMutation.isPending}
                className={currentFavoriteState ? 'text-red-500' : 'text-gray-400'}
                aria-label={currentFavoriteState ? 'Remove from favorites' : 'Add provider to favorites'}
                title={currentFavoriteState ? 'Remove from favorites' : 'Add provider to favorites'}
              >
                <Heart
                  className={`w-5 h-5 ${currentFavoriteState ? 'fill-red-500' : ''}`}
                />
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {displayedSpecialties.map((item) => (
              <Badge
                key={item.specialty.id}
                variant="outline"
                className="text-xs"
              >
                {item.specialty.name}
              </Badge>
            ))}
          </div>

          <div className="flex gap-3">
            {isFinancialAdvisor ? (
              <Button className="bg-[#026857] hover:bg-[#025247] flex-1" asChild>
                <a href={contactHref} onClick={handleGuestContactGate}>
                  {contactLabel}
                </a>
              </Button>
            ) : (
              <Button
                className="bg-[#026857] hover:bg-[#025247] flex-1"
                onClick={handleContactClick}
              >
                {contactLabel}
              </Button>
            )}

            {canSchedule && (
              <Button
                variant="outline"
                className="flex-1"
                onClick={() =>
                  onSchedule?.({
                    id: providerId,
                    name: displayName,
                    calendlyUrl: calendlyUrl || null,
                    intakeFormUrl: intakeFormUrl || null,
                  })
                }
              >
                {/* Feature 102: cards are primary-only — The Key keeps its
                    Schedule label (intake form), everyone else requests a call. */}
                {contactAction.primary === 'intake-form' ? (
                  <>
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule
                  </>
                ) : (
                  <>
                    <Phone className="w-4 h-4 mr-2" />
                    Request a call
                  </>
                )}
              </Button>
            )}

            <Button
              variant="outline"
              className="flex-1"
              asChild
            >
              <Link to={detailsHref} onClick={handleGuestProfileGate}>
                View Details
              </Link>
            </Button>
          </div>
        </div>
      </div>
      {!isFinancialAdvisor && messageModal}
      {canSendContactCard && contactCardOpen && (
        // Mounted only while open. The card knows the provider's id and name but
        // not their phone, email or title, so the modal fetches those itself —
        // and a directory page renders ten of these, each of which would
        // otherwise mount a dialog and a React Query subscription to sit idle.
        <ShareContactCardModal
          open
          onOpenChange={setContactCardOpen}
          serviceProviderId={providerId}
        />
      )}
    </Card>
  );
};